import clsx from 'clsx'
import ActionsMenu, { ActionsMenuItem } from 'components/ActionsMenu/ActionsMenu'
import { Button } from 'components/Button/Button'
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog'
import { ReactComponent as CalculateRouteSvg } from 'images/icons/calculate_route.svg'
import { ReactComponent as RemoveSvg } from 'images/icons/delete.svg'
import { ReactComponent as FinishPointSvg } from 'images/icons/transfer_route_finish.svg'
import { ReactComponent as MiddleRouteSvg } from 'images/icons/transfer_route_middle.svg'
import { ReactComponent as StartPointSvg } from 'images/icons/transfer_route_start.svg'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { selectHighlightedFeatureIdInList, selectMapMode, selectMissionInfo } from 'store/selectors/mission'
import { MISSION_MAP_DRAWING_MODES, TRANSFER_CALCULATING_STATUSES, TRANSFER_TYPE } from 'utils/constants'
import { isComboOrOmniRobotType } from 'utils/robotTypeDisplayName'
import formatDuration from 'utils/formatDuration'
import getTestId from 'utils/getTestId'

import style from './CommonRouteItem.module.css'

type CommonRouteItemProps = {
  text?: string
  itemType: TRANSFER_TYPE
  id: string | number
  editing: boolean
  status?: TRANSFER_CALCULATING_STATUSES
  length?: number
  executionTime?: number
  onClick: () => void
  onEdit: () => void
  onRemove: () => void
  onCancel: (e?: any) => void
  onSave: (e?: any) => void
  hideActions?: boolean
  onCalculate?: () => void
  onUseRecordedRoute?: () => void
  isRoute?: boolean
}

const CommonRouteItem = (props: CommonRouteItemProps) => {
  const itemRef = useRef<HTMLDivElement>(null)
  const { t } = useTranslation()
  const highlightedFeatureIdInList = useSelector(selectHighlightedFeatureIdInList)
  const missionInfo = useSelector(selectMissionInfo)
  const mapMode = useSelector(selectMapMode)
  const isEditingTransfer = mapMode === MISSION_MAP_DRAWING_MODES.DIRECT_SELECT_TRANSFER_ROUTE
  const [showConfirmDelete, setShowConfirmDelete] = useState(false)

  const hideCleaningType = missionInfo ? !isComboOrOmniRobotType(missionInfo.robot_type) : false
  const highlighted = useMemo(() => {
    return highlightedFeatureIdInList === props.id
  }, [highlightedFeatureIdInList, props.id])

  useEffect(() => {
    if (highlighted && itemRef.current) {
      itemRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }
  }, [highlighted])

  const routeItemStyle = hideCleaningType ? style.transferRouteItemShort : style.transferRouteItem
  const icon = useMemo(() => {
    if (props.isRoute) {
      return <MiddleRouteSvg className={style.iconMiddle} />
    }
    switch (props.itemType) {
      case TRANSFER_TYPE.START:
        return <StartPointSvg className={style.iconStart} />
      case TRANSFER_TYPE.MIDDLE:
        return <MiddleRouteSvg className={style.iconMiddle} />
      case TRANSFER_TYPE.FINISH:
        return <FinishPointSvg className={style.iconFinish} />
    }
  }, [props.itemType, props.isRoute])

  const confirmTitle = useMemo(() => {
    if (props.isRoute) return t('mission.delete_route_confirm', { name: props.text })
    switch (props.itemType) {
      case TRANSFER_TYPE.START:
        return t('mission.delete_start_point_confirm')
      case TRANSFER_TYPE.FINISH:
        return t('mission.delete_finish_point_confirm')
      case TRANSFER_TYPE.MIDDLE:
        return t('mission.delete_route_confirm', { name: props.text })
      default:
        return `${t('common.delete')}?`
    }
  }, [props.itemType, props.isRoute, props.text, t])

  return (
    <div
      ref={itemRef}
      className={highlighted ? clsx(routeItemStyle, style.highlighted) : routeItemStyle}
      onClick={props.onClick}
    >
      <div></div>
      <div></div>
      <div className={style.icon}>{icon}</div>
      <div className={style.text}>{props.text}</div>
      {!hideCleaningType && <div className={style.text}>{}</div>}
      {props.editing ? (
        <div className={style.buttonsBlock} style={{ gridColumn: 'span 2' }}>
          <Button.Outlined className={style.smallBtn} onClick={props.onCancel}>
            {t('common.cancel')}
          </Button.Outlined>
          <Button.Outlined className={style.smallBtn} onClick={props.onSave}>
            {t('common.save')}
          </Button.Outlined>
        </div>
      ) : props.status === TRANSFER_CALCULATING_STATUSES.OK && (props.length || props.executionTime) ? (
        <>
          <div className={style.text}>
            {props.length && <span className={style.bold}>{Math.round(props.length)}m</span>}
          </div>
          <div className={style.text}>
            {props.executionTime && <span className={style.bold}>{formatDuration(props.executionTime)}</span>}
          </div>
        </>
      ) : (
        <div className={style.buttonsBlock} style={{ gridColumn: 'span 2' }}>
          {props.status && t('mission_status.' + props.status)}
        </div>
      )}
      <div className={style.zoneItemColSmall}>
        {!props.hideActions && (
          <ActionsMenu disabled={isEditingTransfer}>
            {props.onCalculate && (
              <ActionsMenuItem
                onClick={props.onCalculate}
                data-test-id={getTestId('calc-route')}
                disabled={
                  isEditingTransfer ||
                  props.status === TRANSFER_CALCULATING_STATUSES.OK ||
                  props.status === TRANSFER_CALCULATING_STATUSES.CALCULATING
                }
              >
                <span className={style.menuItemIcon} />
                <span data-test-id={getTestId('calc-title')}>{t('mission.calculate_route')}</span>
              </ActionsMenuItem>
            )}
            <ActionsMenuItem
              onClick={props.onEdit}
              data-test-id={getTestId('edit-zone-list')}
              disabled={isEditingTransfer}
            >
              <span className={style.menuItemIcon} />
              <span data-test-id={getTestId('edit-title')}>{t('common.edit')}</span>
            </ActionsMenuItem>
            {props.onUseRecordedRoute && (
              <ActionsMenuItem
                onClick={props.onUseRecordedRoute}
                data-test-id={getTestId('use-recorded-route')}
                disabled={isEditingTransfer}
              >
                <span className={style.menuItemIcon} />
                <span data-test-id={getTestId('use-recorded-route-title')}>{t('mission.use_recorded_route')}</span>
              </ActionsMenuItem>
            )}
            <ActionsMenuItem
              onClick={() => setShowConfirmDelete(true)}
              data-test-id={getTestId('remove-zone-list')}
              disabled={isEditingTransfer}
            >
              <RemoveSvg className={style.menuItemIcon} />
              <span data-test-id={getTestId('delete-title')}>{t('common.delete')}</span>
            </ActionsMenuItem>
          </ActionsMenu>
        )}
      </div>
      <ConfirmDialog
        isOpen={showConfirmDelete}
        onClose={() => setShowConfirmDelete(false)}
        onConfirmClick={() => {
          props.onRemove()
          setShowConfirmDelete(false)
        }}
        title={confirmTitle}
        confirmText={t('common.delete')}
      />
    </div>
  )
}

export default CommonRouteItem
